import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { radioUI } from 'platform/forms-system/src/js/web-component-patterns';

const methods = ['text', 'email', 'mail'];

const NoMobilePhoneAlertDescription = () => {
  const mebNewContactInfoUrls = useSelector(
    state => state.form?.data?.mebNewContactInfoUrls,
  );
  const phoneAndEmailPath = mebNewContactInfoUrls
    ? 'phone-email'
    : 'phone-and-email';

  return (
    <va-alert slim status="warning">
      We can’t send you text message notifications because we don’t have a
      mobile phone number on file for you.
      <Link
        aria-label="Go back and add a mobile phone number"
        to={{
          pathname: phoneAndEmailPath,
        }}
      >
        <va-button
          secondary
          text="Go back and add a mobile phone number"
          class="vads-u-margin-top--1"
        />
      </Link>
    </va-alert>
  );
};

const monthlyEnrollment = {
  uiSchema: {
    'view:monthlyEnrollmentIntro': {
      'ui:description': (
        <>
          <h3 className="meb-form-page-only">
            Choose how you want to verify your monthly enrollment
          </h3>
          <p>
            You’ll be prompted to verify your enrollment every month so you’ll
            receive your education payments.
          </p>
          <p>
            <strong>Note:</strong> You can update your preference in your VA
            profile at any time.
          </p>
          <va-link
            href="/profile"
            text="Go to your profile"
            class="vads-u-margin-top--2"
          />
        </>
      ),
    },
    'view:noMobilePhoneAlert': {
      'ui:description': NoMobilePhoneAlertDescription,
      'ui:options': {
        hideIf: formData => {
          const mobilePhone = formData?.contactInfo?.mobilePhone?.contact;
          const wantsTexts =
            formData?.monthlyEnrollmentVerificationMethod?.includes('text');

          return !!mobilePhone || !wantsTexts;
        },
      },
    },
    monthlyEnrollmentVerificationMethod: {
      ...radioUI({
        title: 'How would you like to verify your monthly enrollment?',
        tile: true,
        descriptions: {
          text: 'We’ll send you a text every month asking you to verify your enrollment.',
          email: 'We’ll email you to verify your enrollment each month.',
          mail: 'You’ll receive all communications, including important updates about your decision, through the mail.',
        },
      }),
      'ui:validations': [
        (errors, field, formData) => {
          const wantsTexts = field.includes('text');
          const phoneExist = !!formData?.contactInfo?.mobilePhone?.contact;
          const isInternational = !['US', 'PR'].includes(
            formData?.contactInfo?.mobilePhone?.countryCode,
          );
          if (wantsTexts && !phoneExist) {
            errors.addError(
              'You can’t select that response because we don’t have a mobile phone number on file for you',
            );
          } else if (wantsTexts && isInternational) {
            errors.addError(
              'You can’t select that response because you have an international mobile phone number',
            );
          }
        },
      ],
    },
    'view:internationalTextMessageAlert': {
      'ui:description': (
        <va-alert slim status="warning">
          You can’t choose to get text message notifications because you have an
          international mobile phone number. At this time, we can send text
          messages about your education benefits only to U.S. mobile phone
          numbers.
        </va-alert>
      ),
      'ui:options': {
        hideIf: formData => {
          const mobilePhone = formData?.contactInfo?.mobilePhone?.contact;
          const isInternational = !['US', 'PR'].includes(
            formData?.contactInfo?.mobilePhone?.countryCode,
          );
          const wantsTexts =
            formData.monthlyEnrollmentVerificationMethod === 'text';
          return !mobilePhone || !isInternational || !wantsTexts;
        },
      },
    },
    'view:emailOnFileWithSomeoneElse': {
      'ui:description': (
        <va-alert slim status="warning">
          You can’t choose to get email notifications because your email is on
          file for another person with education benefits. You will not be able
          to take full advantage of VA’s electronic notifications and enrollment
          verifications available. If you cannot, certain electronic services
          will be limited or unavailable.
          <va-link
            href="/education/verify-school-enrollment"
            text="Learn more about Enrollment Verifications"
            class="vads-u-display--block vads-u-margin-top--2"
          />
        </va-alert>
      ),
      'ui:options': {
        hideIf: formData => {
          const wantsEmails =
            formData.monthlyEnrollmentVerificationMethod === 'email';
          const duplicatesDetected = formData?.duplicateEmail?.some(
            entry => !!entry?.dupe,
          );
          return !wantsEmails || !duplicatesDetected;
        },
      },
    },
    'view:mobilePhoneOnFileWithSomeoneElse': {
      'ui:description': (
        <va-alert slim status="warning">
          You can’t choose to get text notifications because your mobile phone
          number is on file for another person with education benefits. You will
          not be able to take full advantage of VA’s electronic notifications
          and enrollment verifications available. If you cannot, certain
          electronic services will be limited or unavailable.
          <va-link
            href="/education/verify-school-enrollment"
            text="Learn more about Enrollment Verifications"
            class="vads-u-display--block vads-u-margin-top--2"
          />
        </va-alert>
      ),
      'ui:options': {
        hideIf: formData => {
          const wantsTexts =
            formData.monthlyEnrollmentVerificationMethod === 'text';
          const duplicatesDetected = formData?.duplicatePhone?.some(
            entry => !!entry?.dupe,
          );
          const mobilePhone = formData?.contactInfo?.mobilePhone?.contact;

          return !wantsTexts || !duplicatesDetected || !mobilePhone;
        },
      },
    },
    'view:textMessageDisclaimer': {
      'ui:description': (
        <va-additional-info trigger="What to know about text notifications">
          <p>
            Message and data rates may apply. If you want to opt out, text STOP.
            IF you need help, text HELP.
          </p>
        </va-additional-info>
      ),
    },
  },
  schema: {
    type: 'object',
    properties: {
      'view:monthlyEnrollmentIntro': {
        type: 'object',
        properties: {},
      },
      'view:noMobilePhoneAlert': {
        type: 'object',
        properties: {},
      },
      monthlyEnrollmentVerificationMethod: {
        type: 'string',
        enum: methods,
        enumNames: [
          'Text (Recommended method)',
          'Email',
          'I don’t want electronic notifications',
        ],
      },
      'view:internationalTextMessageAlert': {
        type: 'object',
        properties: {},
      },
      'view:emailOnFileWithSomeoneElse': {
        type: 'object',
        properties: {},
      },
      'view:mobilePhoneOnFileWithSomeoneElse': {
        type: 'object',
        properties: {},
      },
      'view:textMessageDisclaimer': {
        type: 'object',
        properties: {},
      },
    },
    required: ['monthlyEnrollmentVerificationMethod'],
  },
};

export default monthlyEnrollment;
